import {
    CalendarDays, ChevronDown, ChevronRight, CircleCheck, FileText, Receipt,
    Stethoscope, Wrench, type LucideIcon,
} from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';

/**
 * The seven-step work loop, drawn.
 *
 * Built from layout primitives rather than a fixed-viewBox SVG so the labels
 * reflow, scale with the user's text size, follow the light/dark theme, and
 * pick up the vertical's jargon. An exported image would need redrawing every
 * time a label or a colour changed — which is exactly the staleness this whole
 * pass has been fixing.
 *
 * Two variants:
 *   'full'  — vertical on phones, horizontal from lg up, with notes + caption.
 *             For the help article, where the reader came to study it.
 *   'chips' — wrapped inline chips, no notes. For the welcome carousel, where
 *             it sits inside a narrow card and only needs to convey the shape.
 */

interface Step {
    icon: LucideIcon;
    label: string;
    note: string;
    /** The step where money actually moves. */
    terminal?: boolean;
}

export const FlowDiagram = ({ variant = 'full' }: { variant?: 'full' | 'chips' }) => {
    const { molded } = useVertical();
    const { jargon } = molded;

    const steps: Step[] = [
        { icon: CalendarDays, label: 'Appointment', note: 'to diagnose' },
        { icon: Stethoscope, label: 'Diagnostic', note: 'findings + photos' },
        { icon: FileText, label: 'Quote', note: 'priced, agreed' },
        { icon: CalendarDays, label: 'Appointment', note: 'to do the work' },
        { icon: Wrench, label: jargon.action_label, note: 'the work itself' },
        { icon: Receipt, label: 'Invoice', note: 'what you are owed' },
        { icon: CircleCheck, label: 'Paid', note: 'money moves', terminal: true },
    ];

    if (variant === 'chips') {
        return (
            <ol
                className="flex flex-wrap items-center gap-x-1.5 gap-y-2 my-4"
                aria-label="The work loop, seven steps"
            >
                {steps.map((step, i) => (
                    <li key={`${step.label}-${i}`} className="flex items-center gap-1.5">
                        <span
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${
                                step.terminal
                                    ? 'bg-success/12 border-success/40'
                                    : 'bg-gold-fill/10 border-gold/25'
                            }`}
                        >
                            <step.icon
                                className={`w-3.5 h-3.5 shrink-0 ${
                                    step.terminal ? 'text-success' : 'text-gold'
                                }`}
                                strokeWidth={2}
                            />
                            <span
                                className={`text-[12px] font-semibold whitespace-nowrap ${
                                    step.terminal ? 'text-success' : 'text-fg'
                                }`}
                            >
                                {step.label}
                            </span>
                        </span>
                        {i < steps.length - 1 && (
                            <ChevronRight
                                className="w-3.5 h-3.5 shrink-0 text-fg-4"
                                aria-hidden="true"
                            />
                        )}
                    </li>
                ))}
            </ol>
        );
    }

    return (
        <figure className="my-6 rounded-xl border border-line bg-panel p-4 lg:p-5">
            <ol
                className="flex flex-col lg:flex-row lg:items-center gap-0.5 lg:gap-0"
                aria-label="The work loop, seven steps"
            >
                {steps.map((step, i) => (
                    <li
                        key={`${step.label}-${i}`}
                        className="flex flex-col lg:flex-row lg:items-center lg:flex-1 min-w-0"
                    >
                        <div
                            className={`flex lg:flex-col items-center gap-3 lg:gap-2 lg:text-center px-2 lg:px-1 py-2 rounded-lg lg:flex-1 min-w-0 ${
                                step.terminal ? 'bg-success/10' : ''
                            }`}
                        >
                            <span
                                className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center border ${
                                    step.terminal
                                        ? 'bg-success/15 border-success/40'
                                        : 'bg-gold-fill/10 border-gold/25'
                                }`}
                            >
                                <step.icon
                                    className={`w-[18px] h-[18px] ${
                                        step.terminal ? 'text-success' : 'text-gold'
                                    }`}
                                    strokeWidth={2}
                                />
                            </span>
                            <span className="min-w-0 lg:w-full">
                                <span
                                    className={`block text-[13px] font-semibold leading-tight truncate lg:whitespace-normal ${
                                        step.terminal
                                            ? 'text-success'
                                            : 'text-fg'
                                    }`}
                                >
                                    {step.label}
                                </span>
                                <span className="block text-[11px] text-fg-3 leading-tight mt-0.5">
                                    {step.note}
                                </span>
                            </span>
                        </div>

                        {/* Connector — points down when stacked, right when in a row. */}
                        {i < steps.length - 1 && (
                            <>
                                <ChevronDown
                                    className="lg:hidden w-4 h-4 shrink-0 self-start text-fg-4 ml-[1.05rem]"
                                    aria-hidden="true"
                                />
                                <ChevronRight
                                    className="hidden lg:block w-4 h-4 shrink-0 text-fg-4"
                                    aria-hidden="true"
                                />
                            </>
                        )}
                    </li>
                ))}
            </ol>

            <figcaption className="mt-3 pt-3 border-t border-line text-[12px] text-fg-3 leading-snug">
                Two appointments — one to find out what&apos;s wrong, one to put it right, with an
                agreed price in between. Your tax, NI and VAT reserves move at{' '}
                <strong className="text-success font-semibold">Paid</strong>, not at
                Invoice.
            </figcaption>
        </figure>
    );
};
